<template>
  <div class="edit-container">
    <DefaultHeader title="Редактирование вакансии" :back="true" />

    <div class="form-wrapper">
        <div class="title-row">
            <TextField
            placeholder="Наименование"
            v-model="vacancy.title"
            class="title-input"
            />
            <button
              :disabled="!hasChanges"
              class="save-btn"
              @click="saveVacancy"
            >
              Сохранить
            </button>
        </div>

        <div class="description-row">
        <textarea
            v-model="vacancy.info"
            class="description-textarea"
            placeholder="Описание вакансии"
        ></textarea>
        </div>

        <div class="salary-block">

        <div class="experience-section">
            <label class="experience-label">Необходимый опыт</label>
            <span class="experience-value">
            {{ vacancy.experience === 0 ? "Без опыта" : vacancy.experience }}
            </span>
            <input
            type="range"
            min="0"
            max="10"
            step="1"
            v-model="vacancy.experience"
            class="experience-slider"
            />
        </div>

        <TextField
            placeholder="Зарплата"
            v-model="vacancy.salary"
            class="salary-input"
        />

        <div class="currency-selector">
            <button
            v-for="curr in currencies"
            :key="curr"
            :class="['currency-button', { active: vacancy.currency === curr }]"
            @click="vacancy.currency = curr"
            >
            {{ curr }}
            </button>
        </div>
        <SpecificDropdown v-model="vacancy.specific" />       
        </div>

        <div class="hashtag-section" @click="openHashtagDrawer">
            <h3 class="hashtag-title">Хэштеги</h3>
            <div class="hashtag-button">
                <span class="text">Изменить хэштеги</span>
                <span class="arrow">›</span>
            </div>
        </div>    
        
        <div class="address-block">
            <label class="manual-toggle">
                Указать адрес вручную
                <input type="checkbox" v-model="isManual" />
                <span class="toggle-slider"></span>
            </label>

            <input
                type="text"
                class="address-input"
                :placeholder="isManual ? 'Улица Пятигорского 14' : 'Адрес недоступен'"
                :disabled="!isManual"
                v-model="address"
            />

            <div class="map-placeholder" :class="{ disabled: !isManual }">
                <iframe
                class="map-iframe"
                :src="`https://www.google.com/maps/embed/v1/place?key=AIzaSyBSEN-wo__FMd5MeQUIkQYGFPuqZeHgVNY&q=${encodeURIComponent(addressToShow)}`"
                allowfullscreen
                ></iframe>
            </div>
        </div>

        <div class="image-upload-section" @dragover.prevent @drop.prevent="handleDrop">
            <label
                class="drop-area"
                :class="{ loading: loading }"
                @click="triggerFileInput"
            >
                <input
                type="file"
                accept="image/*"
                ref="fileInput"
                class="hidden-input"
                @change="handleFileChange"
                />

                <div v-if="loading" class="drop-placeholder">Загрузка...</div>

                <div v-else-if="!imageUrl" class="drop-placeholder">
                <img src="/upload-icon.png" class="upload-icon" />
                <p>Добавьте изображение или <span class="link-text">перетащите</span></p>
                </div>

                <div v-else class="image-preview">
                <img :src="imageUrl" alt="Превью изображения" />
                <button class="remove-button" @click.stop="removeImage">Удалить</button>
                </div>
            </label>

            <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
        </div>

    </div>
    <Footer class="feed-footer" />
  </div>

      <HashtagDrawer
        v-if="isHashtagDrawerOpen"
        :model-value="vacancy.hashtags"
        @update:modelValue="val => vacancy.hashtags = val"
        @close="isHashtagDrawerOpen = false"
    />

</template>

<script setup>
import { ref, computed, reactive, onMounted } from 'vue';
import { watch } from 'vue';
import { useRoute } from 'vue-router';
import DefaultHeader from '../components/DefaultHeader.vue';
import TextField from '../components/TextField.vue';
import SpecificDropdown from '../components/SpecificDropdown.vue';
import Footer from '../components/MiniFooter.vue';
import MyVacanciesService from '../api/MyVacanciesService';
import HashtagDrawer from '../views/HashtagDrawer.vue';

const isHashtagDrawerOpen = ref(false);

const openHashtagDrawer = () => {
  isHashtagDrawerOpen.value = true;
};

const closeHashtagDrawer = () => {
  isHashtagDrawerOpen.value = false;
};

const currencies = ['₽', '$', '€'];
const route = useRoute();
const address = ref('');
const isManual = ref(false);

const fileInput = ref(null);
const imageUrl = ref(null);
const file = ref(null);
const loading = ref(false);
const errorMessage = ref(null);
const originalVacancy = ref({});

const triggerFileInput = () => fileInput.value.click();

const uploadImmediately = async (selectedFile) => {
  if (!selectedFile || !selectedFile.type?.startsWith("image/")) {
    errorMessage.value = "❌ Неверный формат файла";
    return;
  }

  file.value = selectedFile;
  loading.value = true;
  errorMessage.value = null;

  try {
    const previewUrl = URL.createObjectURL(selectedFile);
    imageUrl.value = previewUrl;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      localStorage.setItem("vacancy_preview_image_base64", base64);
    };
    reader.readAsDataURL(selectedFile);

    const uploadedUrl = await MyVacanciesService.uploadVacancyImage(vacancy.id, selectedFile);
    localStorage.setItem("vacancy_image_url", uploadedUrl);
  } catch (e) {
    console.error("❌ Ошибка при загрузке:", e);
    errorMessage.value = "Ошибка при загрузке. Попробуй ещё раз.";
    imageUrl.value = null;
    file.value = null;
  } finally {
    loading.value = false;
  }
};

const handleFileChange = (e) => uploadImmediately(e.target.files[0]);
const handleDrop = (e) => uploadImmediately(e.dataTransfer.files[0]);
const removeImage = () => {
  file.value = null;
  imageUrl.value = null;
  fileInput.value.value = "";
  localStorage.removeItem("vacancy_image_url");
};

const addressToShow = computed(() => {
  return address.value?.trim() || 'Москва';
});

const vacancy = reactive({
  id: '',
  title: '',
  info: '',
  salary: '',
  currency: '₽',
  experience: 3,
  hashtags: []
});

const hasChanges = ref(false);
const isReady = ref(false);

onMounted(() => {
  const stored = localStorage.getItem("vacancy_to_edit");
  if (stored) {
    const parsed = JSON.parse(stored);
    parsed.hashtags = parsed.hashtags || [];

    // клонируем до мутаций
    originalVacancy.value = structuredClone(parsed);

    // только потом мутируем реактивный объект
    Object.assign(vacancy, parsed);

    if (parsed.image_url) {
      imageUrl.value = parsed.image_url;
    }

    const manualFromStorage = localStorage.getItem("vacancy_is_manual");
    const addrFromStorage = localStorage.getItem("vacancy_address");
    if (manualFromStorage) isManual.value = manualFromStorage === "true";
    if (addrFromStorage) address.value = addrFromStorage;

    // 🧠 только теперь запускаем слежение
    watch(
      vacancy,
      () => {
        const current = JSON.stringify(vacancy);
        const original = JSON.stringify(originalVacancy.value);
        hasChanges.value = current !== original;
      },
      { deep: true }
    );

    isReady.value = true;
  }
});

async function saveVacancy() {
  console.log('💾 Сохраняем изменения вакансии:', vacancy);

  try {
    const payload = {
      vacancy_id: vacancy.id,
      title: vacancy.title,
      info: vacancy.info,
      salary: vacancy.salary,
      image_url: imageUrl.value ?? "",
      specific: {
        id: vacancy.specific.id,
        name: vacancy.specific.name,
        eng_name: vacancy.specific.eng_name
      },
      is_remote: vacancy.isRemote,
      street: vacancy.street || "",
      experience: vacancy.experience
    };

    console.log("📦 Payload для обновления:", payload);

    await MyVacanciesService.updateVacancy(payload);

    // ✅ Очистка и обновление оригинала
    localStorage.removeItem("vacancy_to_edit");
    originalVacancy.value = structuredClone(vacancy);
    console.log("✅ Вакансия обновлена");
  } catch (error) {
    console.error("❌ Ошибка при сохранении вакансии:", error);
  }
}

</script>

<style scoped>

.edit-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: var(--bg-main);
}

.form-wrapper {
  flex: 1;
  overflow-y: auto;
  width: 100%;
  height: 100vh;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  scroll-behavior: smooth;
}

body {
  overflow-x: hidden;
}

.field-description {
  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.5;
  margin-bottom: 12px;
}

.title-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  width: 800px;
  margin-left: calc(50% - 400px);
}

.title-input {
  flex: 1;
  width: 800px;
}

.description-row {
  width: 650px;
  display: flex;
  justify-content: center;
  margin-left: calc(50% - 400px);
}

.description-textarea {
  width: 100%;
  max-width: 800px;
  min-height: 480px;
  padding: 16px;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  border: 1px solid var(--input-border);
  border-radius: 12px;
  resize: vertical;
  background-color: var(--input-background);
  color: var(--text-primary);
}

.salary-block {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-left: calc(50% - 400px);
}

.experience-section {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 650px;
}

.experience-label {
  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  color: var(--text-primary);
}

.experience-value {
  position: absolute;
  right: 0;
  top: 0;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;
}

.experience-slider {
  width: 650px;
  appearance: none;
  height: 4px;
  background: var(--progress-registration-bg);
  border-radius: 2px;
  outline: none;
}

.salary-input {
  width: 620px;
}

.currency-selector {
  display: flex;
  background-color: var(--currency-selector-bg);
  border-radius: 999px;
  padding: 4px;
  width: fit-content;
}

.currency-button {
  padding: 6px 18px;
  border: none;
  background: transparent;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  color: var(--currency-button-text);
  cursor: pointer;
  border-radius: 999px;
  transition: all 0.2s ease;
}

.currency-button.active {
  background-color: var(--currency-active-bg);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  color: var(--currency-active-text);
  font-family: "Eastman-Bold", sans-serif;
}

</style>

<style>
html, body, #app {
  height: 100%;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
}

.hashtag-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 660px;
  margin-left: calc(50% - 400px);
  cursor: pointer;
}

.hashtag-title {
  font-family: "Eastman-Bold", sans-serif;
  font-size: 16px;
  color: var(--text-primary);
}

/* Кнопка редактирования */
.hashtag-button {
  background-color: var(--hashtag-button-bg);
  border-radius: 18px;
  padding: 18px 22px;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 16px;
  color: var(--text-primary);
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.2s ease;
  pointer-events: auto;
}


.hashtag-button:hover {
  background-color: var(--hashtag-button-bg-hover) !important;
}

.arrow {
  font-size: 18px;
  color: #c0c0c0;
  transform: translateY(1px);
}

.hashtag-button:hover {
  background-color: #ececec;
}

.arrow {
  font-size: 18px;
  color: #c0c0c0;
  transform: translateY(1px);
}

.address-block {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 660px;
  margin-left: calc(50% - 400px);
}

.manual-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  position: relative;
}

.manual-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: relative;
  width: 42px;
  height: 24px;
  background-color: var(--toggle-slider-bg);
  border-radius: 12px;
  transition: background-color 0.3s;
}

.toggle-slider::before {
  content: "";
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.3s;
}

input:checked + .toggle-slider {
  background-color: #2277E6;
}

input:checked + .toggle-slider::before {
  transform: translateX(18px);
}

.address-input {
  padding: 16px;
  border: none;
  border-radius: 12px;
  background-color: var(--address-input-bg);
  font-size: 14px;
  font-family: "Eastman-Regular", sans-serif;
  color: var(--address-input-text);
}

.address-input:disabled {
  background-color: var(--address-input-disabled-bg);
  color: var(--address-input-disabled-text);
  cursor: not-allowed;
}

.map-iframe {
  width: 100%;
  height: 240px;
  border: none;
  border-radius: 12px;
  margin-top: -10px;
}

.map-placeholder {
  opacity: 1;
  transition: opacity 0.3s;
}

.map-placeholder.disabled {
  opacity: 0.4;
  pointer-events: none;
}

.image-upload-section {
  width: 650px;
  margin-left: calc(50% - 400px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.drop-area {
  width: 100%;
  height: 300px;
  border: 2px dashed var(--border-upload-light);
  border-radius: 16px;
  cursor: pointer;
  background-color: var(--upload-bg-light);
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  text-align: center;
}

[data-theme="dark"] .drop-area {
  border-color: var(--border-upload-dark);
  background-color: var(--upload-bg-dark);
}

.drop-area:hover {
  border-color: #2277E6;
}

.hidden-input {
  display: none;
}

.upload-icon {
  width: 40px;
  margin-bottom: 8px;
  opacity: 0.6;
}

.drop-placeholder p {
  font-size: 14px;
  font-family: "Eastman-Regular", sans-serif;
  color: var(--text-secondary);
}

.link-text {
  color: #2277E6;
  text-decoration: underline;
}

.image-preview {
  width: 500px;
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.image-preview img {
  width: 500px;
  height: 260px;
  object-fit: cover;
  border-radius: 12px;
}

.remove-button {
  position: absolute;
  top: 8px;
  right: -48px;
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 12px;
  font-family: "Eastman-Medium";
  cursor: pointer;
}

.error-text {
  margin-top: 10px;
  color: #e74c3c;
  font-size: 13px;
  font-family: "Eastman-Regular", sans-serif;
}

.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: flex-start;
  align-items: stretch;
  z-index: 1000;
}

.drawer-panel {
  width: 900px;
  background-color: white;
  box-shadow: 4px 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.25s ease-out;
}

@keyframes slideIn {
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
}

.save-btn {
  background-color: #3a82f7;
  color: white;
  border: none;
  padding: 10px 22px;
  border-radius: 14px;
  font-size: 14px;
  font-family: "Eastman-Medium";
  cursor: pointer;
  transition: all 0.2s;
}

.save-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

[data-theme="dark"] .save-btn:disabled {
  background-color: #555;
}

</style>